import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as Tabs from '@radix-ui/react-tabs';
import { 
  X, User, Phone, Mail, MapPin, Calendar, Shield, AlertTriangle, 
  FileText, Download, File, Plus, IdCard, BookOpen, Accessibility,
  Paperclip, Lock, Key, Trash2, XCircle, CheckCircle
} from 'lucide-react';
import api from '../services/api';
import { format, parseISO } from 'date-fns';

/**
 * MODAL DÉTAILS UTILISATEUR - LECTURE SEULE
 * Affichage des informations sans modification
 * Upload/suppression de documents uniquement
 */
const UserDetailModal = ({ 
  user, 
  isOpen, 
  onClose, 
  onShowToast
}) => {
  const [activeTab, setActiveTab] = useState('details');
  const [documents, setDocuments] = useState([]);
  const [error, setError] = useState(null);
  const [isGeneratingContract, setIsGeneratingContract] = useState(false);

  // Initialisation
  useEffect(() => {
    if (user && isOpen) {
      loadDocuments();
    }
  }, [user, isOpen]);

  // Fonctions utilitaires
  const formatDate = (dateString) => {
    if (!dateString) return 'Non renseigné';
    try {
      const date = parseISO(dateString);
      return format(date, 'dd/MM/yyyy');
    } catch (e) {
      return 'Date invalide';
    }
  };

  const showSuccessToast = (message) => {
    if (onShowToast) {
      onShowToast(message, 'success');
    }
  };

  const showErrorToast = (message) => {
    if (onShowToast) {
      onShowToast(message, 'error');
    }
  };

  // Gestion des documents
  const loadDocuments = async () => {
    console.log('🔵 [LOAD DOCUMENTS] Début de loadDocuments');
    
    if (!user || !user.id) {
      console.warn('⚠️ [LOAD DOCUMENTS] Arrêt : user ou user.id manquant');
      return;
    }
    
    try {
      console.log('📂 [LOAD DOCUMENTS] Chargement documents pour utilisateur:', user.id);
      
      const response = await api.users.getDocuments(user.id);
      
      console.log('📦 [LOAD DOCUMENTS] Réponse:', response);
      
      if (response && response.success && response.documents) {
        console.log('✅ [LOAD DOCUMENTS] Documents trouvés:', response.documents.length);
        setDocuments(response.documents);
      } else {
        console.warn('⚠️ [LOAD DOCUMENTS] Aucun document');
        setDocuments([]);
      }
    } catch (error) {
      console.error('❌ [LOAD DOCUMENTS] Erreur:', error);
      setDocuments([]);
    }
    
    console.log('🔵 [LOAD DOCUMENTS] Fin de loadDocuments');
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      const errorMsg = 'Format de fichier non pris en charge.';
      setError(errorMsg);
      showErrorToast(errorMsg);
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      const errorMsg = 'La taille du document ne doit pas dépasser 10 MB';
      setError(errorMsg);
      showErrorToast(errorMsg);
      return;
    }

    const formData = new FormData();
    formData.append('document', file);

    if (onShowToast) {
      onShowToast('Document en cours d\'insertion...', 'loading');
    }

    try {
      const response = await api.users.uploadDocument(user.id, formData, type);

      if (response.success) {
        setError(null);
        showSuccessToast('Document ajouté avec succès');
        await loadDocuments();
      } else {
        const errorMsg = response.message || 'Erreur lors de l\'upload du document';
        showErrorToast(errorMsg);
        setError(errorMsg);
      }
    } catch (error) {
      console.error('Erreur lors de l\'upload du document:', error);
      const errorMsg = 'Erreur lors de l\'upload du document';
      showErrorToast(errorMsg);
      setError(errorMsg);
    }
  };

  const handleDownloadDocument = async (docId, docName) => {
    try {
      const result = await api.users.downloadDocument(user.id, docId);
      if (!result.success) {
        const errorMsg = 'Erreur lors du téléchargement du document';
        setError(errorMsg);
        showErrorToast(errorMsg);
      } else {
        showSuccessToast('Téléchargement initialisé');
      }
    } catch (error) {
      console.error('Erreur téléchargement:', error);
      const errorMsg = 'Erreur lors du téléchargement';
      setError(errorMsg);
      showErrorToast(errorMsg);
    }
  };

  const handleDeleteDocument = async (docId, type) => {
    if (onShowToast) {
      onShowToast('Document en cours de suppression...', 'loading');
    }

    try {
      const result = await api.users.deleteDocument(user.id, docId);

      if (result.success) {
        setError(null);
        showSuccessToast('Document supprimé avec succès');
        await loadDocuments();
      } else {
        const errorMsg = 'Erreur lors de la suppression';
        showErrorToast(errorMsg);
        setError(errorMsg);
      }
    } catch (error) {
      console.error('Erreur suppression:', error);
      const errorMsg = 'Erreur lors de la suppression';
      showErrorToast(errorMsg);
      setError(errorMsg);
    }
  };

  const getDocumentIcon = (doc) => {
    if (!doc || !doc.nom_fichier) return <File size={20} className="text-gray-500" />;

    if (doc.nom_fichier.endsWith('.pdf')) {
      return <FileText size={20} className="text-red-500" />;
    } else if (doc.nom_fichier.match(/\.(doc|docx)$/i)) {
      return <FileText size={20} className="text-blue-700" />;
    } else {
      return <File size={20} className="text-gray-500" />;
    }
  };

  const getPhotoUrl = () => {
    if (user.photo_url) {
      return `${process.env.REACT_APP_API_URL?.replace('/api', '')}${user.photo_url}?t=${Date.now()}`;
    }
    return null;
  };


    const hasExistingContract = () => {
    return documents.filter(doc => doc.type === 'contrat').length > 0;
    };

    // ⬅️ NOUVELLE FONCTION : Générer le contrat utilisateur
    const generateUserContract = async () => {
    try {
        setIsGeneratingContract(true);

        // Préparer les données pour la génération
        const contractData = {
        user: {
            id: user.id,
            first_name: user.first_name,
            last_name: user.last_name,
            email: user.email,
            phone: user.phone,
            address: user.address,
            city: user.city,
            date_of_birth: user.date_of_birth,
            role: user.role
        }
        };

        if (onShowToast) {
        onShowToast('Génération du contrat en cours...', 'loading');
        }

        // Appel API
        const response = await api.users.generateUserContract(contractData);

        if (response.success) {
        showSuccessToast('Contrat généré avec succès');
        
        // Recharger les documents
        await loadDocuments();
        
        } else {
        setError('Erreur lors de la génération du contrat: ' + (response.message || 'Une erreur est survenue'));
        showErrorToast('Erreur lors de la génération du contrat');
        }
    } catch (error) {
        console.error('Erreur lors de la génération du contrat:', error);
        setError('Erreur lors de la génération du contrat');
        showErrorToast('Erreur lors de la génération du contrat');
    } finally {
        setIsGeneratingContract(false);
    }
    };


  return (
    <Dialog.Root open={isOpen} onOpenChange={() => {}}>
      <Dialog.Portal>
        {/* Overlay */}
       <Dialog.Overlay className="fixed inset-0 bg-black/60" style={{ zIndex: 9998 }} />
        

        <Dialog.Content 
          className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-5xl max-h-[90vh] rounded-2xl shadow-2xl border border-gray-100 flex flex-col"
          style={{ zIndex: 9999, backgroundColor: '#fdfcf5' }}
        >
          
         <Tabs.Root value={activeTab} onValueChange={setActiveTab} className="flex flex-col flex-1 min-h-0">
            {/* En-tête fixe */}
            <div className="flex-shrink-0 bg-white rounded-t-2xl">

            <div className="px-6 py-5 border-b border-gray-100 flex justify-between items-center rounded-t-2xl"
             style={{ backgroundColor: '#448696' }}>


                <Dialog.Title className="text-xl font-bold text-white flex items-center gap-3">
                  <User size={20} />
                  <span>Profil utilisateur</span>
                </Dialog.Title>
                <button 
                  onClick={onClose}
                  className="p-2 rounded-lg hover:bg-white/20 transition-colors text-white"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Onglets */}
              <Tabs.List className="flex bg-gray-50 px-6">
                <Tabs.Trigger 
                  value="details"
                  className={`px-4 py-3 font-semibold text-sm flex items-center gap-2 transition-colors ${
                    activeTab === 'details' 
                      ? 'text-[#448696] border-b-2 border-[#448696]' 
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <User size={18} />
                  Informations personnelles
                </Tabs.Trigger>

             

                <Tabs.Trigger 
                  value="documents"
                  className={`px-4 py-3 font-semibold text-sm flex items-center gap-2 transition-colors ${
                    activeTab === 'documents' 
                      ? 'text-[#448696] border-b-2 border-[#448696]' 
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <Paperclip size={18} />
                  Documents
                </Tabs.Trigger>
              </Tabs.List>
            </div>

            {/* Message d'erreur */}
            {error && (
              <div className="mx-6 mt-6 p-4 rounded-xl bg-red-50 border-l-4 border-red-500 flex items-start gap-3 shadow-sm">
                <AlertTriangle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-red-700 font-semibold text-sm">
                  {typeof error === 'string' ? error : error?.message || 'Une erreur est survenue'}
                </p>
              </div>
            )}

            {/* ONGLET DÉTAILS - LECTURE SEULE */}

             <div className="flex-1 overflow-y-auto modal-scroll min-h-0">
            <Tabs.Content value="details">
              <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6 pb-10">
                
                {/* Colonne 1 - Photo et Info Compte */}
                <div className="space-y-6">
                  {/* Photo Card */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                    <h3 className="font-bold text-sm text-gray-700 mb-4">Photo de profil</h3>
                    
                    <div className="flex flex-col items-center">
                      <div className="relative w-40 h-40 rounded-2xl overflow-hidden shadow-md">
                        {getPhotoUrl() ? (
                          <img 
                            src={getPhotoUrl()} 
                            alt={user.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center" 
                               style={{ backgroundColor: '#448696' }}>
                            <User size={64} className="text-white" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Informations compte */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                    <h3 className="font-bold text-sm text-gray-700 mb-4">Informations compte</h3>
                    
                    <div className="space-y-4">
                      {/* Email */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                          Email
                        </label>
                        <div className="bg-gray-50 px-3 py-2.5 rounded-lg border border-gray-100 font-medium text-sm text-gray-900 break-all">
                          {user.email}
                        </div>
                      </div>

                      {/* Rôle */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Rôle</label>
                        <div className="flex items-center gap-2">
                          <span
                            className="inline-block px-3 py-1.5 text-xs font-semibold rounded-lg text-white"
                            style={{
                              backgroundColor: user.role === 'admin' ? '#448696' : user.role === 'manager' ? '#fed774' : '#9ca3af'
                            }}
                          >
                            {user.role === 'admin' ? 'Administrateur' : user.role === 'manager' ? 'Manager' : 'Utilisateur'}
                          </span>
                          {user.is_primary_admin === true || user.is_primary_admin === 1 ? (
                          <span className="text-xs font-semibold text-amber-600 bg-amber-50 px-2 py-1 rounded">
                            Admin principal
                          </span>
                        ) : null}
                        </div>
                      </div>


                      {/* Dernière connexion */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                          Dernière connexion
                        </label>
                        <div className="bg-gray-50 px-3 py-2.5 rounded-lg border border-gray-100 font-medium text-sm text-gray-900">
                          {user.last_login_at 
                            ? formatDate(user.last_login_at)
                            : 'Jamais connecté'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Colonne 2 - Informations personnelles */}
                <div className="space-y-6">
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                    <h3 className="font-bold text-sm text-gray-700 mb-4">Identité</h3>
                    
                    <div className="space-y-4">
                      {/* Prénom */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Prénom</label>
                        <div className="bg-gray-50 px-3 py-2.5 rounded-lg border border-gray-100 font-medium text-sm text-gray-900">
                          {user.first_name || 'Non renseigné'}
                        </div>
                      </div>

                      {/* Nom */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Nom</label>
                        <div className="bg-gray-50 px-3 py-2.5 rounded-lg border border-gray-100 font-medium text-sm text-gray-900">
                          {user.last_name || 'Non renseigné'}
                        </div>
                      </div>

                      {/* Date naissance */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Date de naissance</label>
                        <div className="bg-gray-50 px-3 py-2.5 rounded-lg border border-gray-100 font-medium text-sm text-gray-900">
                          {formatDate(user.date_of_birth)}
                        </div>
                      </div>

                      {/* Sexe */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Sexe</label>
                        <div className="bg-gray-50 px-3 py-2.5 rounded-lg border border-gray-100 font-medium text-sm text-gray-900">
                          {user.gender === 'M' ? 'Masculin' : 'Féminin'}
                        </div>
                      </div>

                      {/* Téléphone */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Téléphone</label>
                        <div className="bg-gray-50 px-3 py-2.5 rounded-lg border border-gray-100 font-medium text-sm text-gray-900">
                          {user.phone || 'Non renseigné'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Colonne 3 - Adresse */}
                <div className="space-y-6">
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                    <h3 className="font-bold text-sm text-gray-700 mb-4">Adresse</h3>
                    
                    <div className="space-y-4">
                      {/* Adresse complète */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Adresse complète</label>
                        <div className="bg-gray-50 px-3 py-2.5 rounded-lg border border-gray-100 font-medium text-sm text-gray-900 min-h-[4rem]">
                          {user.address || 'Non renseigné'}
                        </div>
                      </div>

                      {/* Ville */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Ville</label>
                        <div className="bg-gray-50 px-3 py-2.5 rounded-lg border border-gray-100 font-medium text-sm text-gray-900">
                          {user.city || 'Non renseigné'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Informations système */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                    <h3 className="font-bold text-sm text-gray-700 mb-4">Informations système</h3>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                          Créé le
                        </label>
                        <div className="bg-gray-50 px-3 py-2.5 rounded-lg border border-gray-100 font-medium text-sm text-gray-900">
                          {user.created_at 
                            ? new Date(user.created_at).toLocaleString('fr-FR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                            : 'Non renseigné'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Tabs.Content>       

            {/* ONGLET DOCUMENTS - AVEC UPLOAD/DELETE */}
            <Tabs.Content value="documents">
              <div className="p-6 space-y-6 pb-10">
                

                   {/* SECTION CONTRAT */}
                <div className="bg-white rounded-xl overflow-hidden shadow-md border border-gray-100">
                <div className="px-5 py-4 bg-gradient-to-r from-[#448696] to-[#6fa3b0] border-b border-[#5f8f9b]">
                    <h3 className="font-bold text-white flex items-center gap-2 text-base">
                    <FileText size={20} />
                    Contrat de Travail
                    </h3>
                </div>
                
                <div className="p-6">
                    <div className="grid md:grid-cols-2 gap-6">
                    
                    {/* Zone génération */}
                    <div className="space-y-4">
                        <h4 className="font-semibold text-gray-700 text-sm">Générer un nouveau contrat</h4>
                        
                        {/* Checklist */}
                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <p className="text-xs font-semibold text-gray-600 mb-3">Informations requises :</p>
                        <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm">
                            {user.first_name && user.last_name ? 
                                <CheckCircle size={16} className="text-green-500" /> : 
                                <XCircle size={16} className="text-red-500" />
                            }
                            <span className={user.first_name && user.last_name ? 'text-green-700 font-medium' : 'text-red-700'}>
                                Nom et prénom
                            </span>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                            {user.email ? 
                                <CheckCircle size={16} className="text-green-500" /> : 
                                <XCircle size={16} className="text-red-500" />
                            }
                            <span className={user.email ? 'text-green-700 font-medium' : 'text-red-700'}>
                                Email
                            </span>
                            </div>
                        </div>
                        </div>
                        
                        {/* Bouton génération */}
                        <button
                        onClick={generateUserContract}
                        disabled={isGeneratingContract || hasExistingContract() || !user.first_name || !user.last_name || !user.email}
                        className={`w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed ${
                            hasExistingContract() 
                            ? 'bg-gray-400 text-white' 
                            : 'bg-[#448696] text-white hover:-translate-y-0.5'
                        }`}
                        >
                        {isGeneratingContract ? (
                            <>
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Génération en cours...
                            </>
                        ) : hasExistingContract() ? (
                            <>
                            <FileText size={18} />
                            Contrat déjà généré
                            </>
                        ) : (
                            <>
                            <FileText size={18} />
                            Générer le contrat
                            </>
                        )}
                        </button>
                        
                        {hasExistingContract() && (
                        <p className="text-xs text-blue-600 bg-blue-50 p-3 rounded-lg border border-blue-100">
                        Un contrat existe déjà. Vous pouvez le télécharger ou le supprimer si nécessaire.
                        </p>
                        )}
                    </div>
                    
                    {/* Liste contrats */}
                    <div className="space-y-4">
                        <h4 className="font-semibold text-gray-700 text-sm">Contrats existants</h4>
                        
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 min-h-[200px]">
                        {documents.filter(doc => doc.type === 'contrat').length > 0 ? (
                            <div className="space-y-2">
                            {documents.filter(doc => doc.type === 'contrat').map((doc, index) => (
                                <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:shadow-sm transition-shadow">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                                    <FileText size={20} className="text-blue-600" />
                                    </div>
                                    <div>
                                    <p className="text-sm font-semibold text-gray-800">
                                        {doc.nom_fichier || `Contrat ${index + 1}`}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        {doc.date_ajout ? new Date(doc.date_ajout).toLocaleDateString('fr-FR') : 'Date inconnue'}
                                    </p>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                    onClick={() => handleDownloadDocument(doc.id, doc.nom_fichier)}
                                    className="p-2 hover:bg-blue-50 rounded-lg transition-colors group"
                                    title="Télécharger"
                                    >
                                    <Download size={16} className="text-blue-600 group-hover:text-blue-700" />
                                    </button>
                                    <button
                                    onClick={() => handleDeleteDocument(doc.id, doc.type)}
                                    className="p-2 hover:bg-red-50 rounded-lg transition-colors group"
                                    title="Supprimer"
                                    >
                                    <Trash2 size={16} className="text-red-600 group-hover:text-red-700" />
                                    </button>
                                </div>
                                </div>
                            ))}
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center h-full py-8">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                                <FileText size={32} className="text-gray-300" />
                            </div>
                            <p className="text-sm text-gray-500">Aucun contrat disponible</p>
                            </div>
                        )}
                        </div>
                    </div>
                    </div>
                </div>
                </div>


                  {/* SECTION PIÈCE D'IDENTITÉ */}
                <div className="bg-white rounded-xl overflow-hidden shadow-md border border-gray-100">
                  <div className="px-5 py-4 bg-gradient-to-r from-[#448696] to-[#6fa3b0] border-b border-[#5f8f9b] flex justify-between items-center">
                    <h3 className="font-bold text-white flex items-center gap-2 text-base">
                      <Accessibility size={20} />
                      Pièce d'Identité
                    </h3>
                    <button
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'application/pdf,image/jpeg,image/png,image/jpg';
                        input.onchange = (e) => handleFileUpload(e, 'piece_identite');
                        input.click();
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors text-white font-semibold text-sm backdrop-blur-sm"
                    >
                      <Plus size={16} />
                      Ajouter
                    </button>
                  </div>
                  
                  <div className="p-6">
                    {documents.filter(doc => doc.type === 'piece_identite').length > 0 ? (
                      <div className="space-y-3">
                        {documents.filter(doc => doc.type === 'piece_identite').map((doc, index) => (
                          <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200 hover:shadow-sm transition-shadow">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center">
                                {getDocumentIcon(doc)}
                              </div>
                              <div>
                                <p className="font-semibold text-gray-800 text-sm">
                                  {doc.nom_fichier || `Document ${index + 1}`}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {doc.date_ajout ? new Date(doc.date_ajout).toLocaleDateString('fr-FR') : 'Date inconnue'}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleDownloadDocument(doc.id, doc.nom_fichier)}
                                className="p-2 hover:bg-blue-50 rounded-lg transition-colors group"
                                title="Télécharger"
                              >
                                <Download size={16} className="text-blue-600 group-hover:text-blue-700" />
                              </button>
                              <button
                                onClick={() => handleDeleteDocument(doc.id, doc.type)}
                                className="p-2 hover:bg-red-50 rounded-lg transition-colors group"
                                title="Supprimer"
                              >
                                <Trash2 size={16} className="text-red-600 group-hover:text-red-700" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl py-12 flex flex-col items-center">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                          <Accessibility size={32} className="text-gray-300" />
                        </div>
                        <p className="text-gray-500 mb-4 font-medium">Aucune pièce d'identité</p>
                        <button
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'application/pdf,image/jpeg,image/png,image/jpg';
                            input.onchange = (e) => handleFileUpload(e, 'piece_identite');
                            input.click();
                          }}
                          className="flex items-center gap-2 px-5 py-2.5 bg-[#448696] text-white rounded-xl font-semibold text-sm hover:shadow-lg hover:-translate-y-0.5 transition-all"
                        >
                          <Plus size={16} />
                          Ajouter une pièce d'identité
                        </button>
                      </div>
                    )}
                  </div>
                </div>
            

                {/* SECTION CV */}
                <div className="bg-white rounded-xl overflow-hidden shadow-md border border-gray-100">
                  <div className="px-5 py-4 bg-gradient-to-r from-[#448696] to-[#6fa3b0] border-b border-[#5f8f9b] flex justify-between items-center">
                    <h3 className="font-bold text-white flex items-center gap-2 text-base">
                      <FileText size={20} />
                      Curriculum Vitae
                    </h3>
                    <button
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
                        input.onchange = (e) => handleFileUpload(e, 'cv');
                        input.click();
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors text-white font-semibold text-sm backdrop-blur-sm"
                    >
                      <Plus size={16} />
                      Ajouter
                    </button>
                  </div>
                  
                  <div className="p-6">
                    {documents.filter(doc => doc.type === 'cv').length > 0 ? (
                      <div className="space-y-3">
                        {documents.filter(doc => doc.type === 'cv').map((doc, index) => (
                          <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200 hover:shadow-sm transition-shadow">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                                {getDocumentIcon(doc)}
                              </div>
                              <div>
                                <p className="font-semibold text-gray-800 text-sm">
                                  {doc.nom_fichier || `Document ${index + 1}`}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {doc.date_ajout ? new Date(doc.date_ajout).toLocaleDateString('fr-FR') : 'Date inconnue'}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleDownloadDocument(doc.id, doc.nom_fichier)}
                                className="p-2 hover:bg-blue-50 rounded-lg transition-colors group"
                                title="Télécharger"
                              >
                                <Download size={16} className="text-blue-600 group-hover:text-blue-700" />
                              </button>
                              <button
                                onClick={() => handleDeleteDocument(doc.id, doc.type)}
                                className="p-2 hover:bg-red-50 rounded-lg transition-colors group"
                                title="Supprimer"
                              >
                                <Trash2 size={16} className="text-red-600 group-hover:text-red-700" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl py-12 flex flex-col items-center">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                          <FileText size={32} className="text-gray-300" />
                        </div>
                        <p className="text-gray-500 mb-4 font-medium">Aucun CV ajouté</p>
                        <button
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
                            input.onchange = (e) => handleFileUpload(e, 'cv');
                            input.click();
                          }}
                          className="flex items-center gap-2 px-5 py-2.5 bg-[#448696] text-white rounded-xl font-semibold text-sm hover:shadow-lg hover:-translate-y-0.5 transition-all"
                        >
                          <Plus size={16} />
                          Ajouter un CV
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* SECTION DIPLÔMES */}
                <div className="bg-white rounded-xl overflow-hidden shadow-md border border-gray-100">
                  <div className="px-5 py-4 bg-gradient-to-r from-[#448696] to-[#6fa3b0] border-b border-[#5f8f9b] flex justify-between items-center">
                    <h3 className="font-bold text-white flex items-center gap-2 text-base">
                      <BookOpen size={20} />
                      Diplômes et Certifications
                    </h3>
                    <button
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.multiple = true;
                        input.accept = 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
                        input.onchange = (e) => handleFileUpload(e, 'diplome');
                        input.click();
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors text-white font-semibold text-sm backdrop-blur-sm"
                    >
                      <Plus size={16} />
                      Ajouter
                    </button>
                  </div>
                  
                  <div className="p-6">
                    {documents.filter(doc => doc.type === 'diplome').length > 0 ? (
                      <div className="space-y-3">
                        {documents.filter(doc => doc.type === 'diplome').map((doc, index) => (
                          <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200 hover:shadow-sm transition-shadow">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                                {getDocumentIcon(doc)}
                              </div>
                              <div>
                                <p className="font-semibold text-gray-800 text-sm">
                                  {doc.nom_fichier || `Document ${index + 1}`}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {doc.date_ajout ? new Date(doc.date_ajout).toLocaleDateString('fr-FR') : 'Date inconnue'}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleDownloadDocument(doc.id, doc.nom_fichier)}
                                className="p-2 hover:bg-blue-50 rounded-lg transition-colors group"
                                title="Télécharger"
                              >
                                <Download size={16} className="text-blue-600 group-hover:text-blue-700" />
                              </button>
                              <button
                                onClick={() => handleDeleteDocument(doc.id, doc.type)}
                                className="p-2 hover:bg-red-50 rounded-lg transition-colors group"
                                title="Supprimer"
                              >
                                <Trash2 size={16} className="text-red-600 group-hover:text-red-700" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl py-12 flex flex-col items-center">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                          <BookOpen size={32} className="text-gray-300" />
                        </div>
                        <p className="text-gray-500 mb-4 font-medium">Aucun diplôme ajouté</p>
                        <button
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.multiple = true;
                            input.accept = 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
                            input.onchange = (e) => handleFileUpload(e, 'diplome');
                            input.click();
                          }}
                          className="flex items-center gap-2 px-5 py-2.5 bg-[#448696] text-white rounded-xl font-semibold text-sm hover:shadow-lg hover:-translate-y-0.5 transition-all"
                        >
                          <Plus size={16} />
                          Ajouter des diplômes
                        </button>
                      </div>
                    )}
                  </div>
                </div>

              

          
             

                {/* SECTION AUTRES DOCUMENTS */}
                <div className="bg-white rounded-xl overflow-hidden shadow-md border border-gray-100">
                  <div className="px-5 py-4 bg-gradient-to-r from-[#448696] to-[#6fa3b0] border-b border-[#5f8f9b] flex justify-between items-center">
                    <h3 className="font-bold text-white flex items-center gap-2 text-base">
                      <File size={20} />
                      Autres Documents
                    </h3>
                    <button
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.multiple = true;
                        input.accept = 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
                        input.onchange = (e) => handleFileUpload(e, 'other');
                        input.click();
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors text-white font-semibold text-sm backdrop-blur-sm"
                    >
                      <Plus size={16} />
                      Ajouter
                    </button>
                  </div>
                  
                  <div className="p-6">
                    {documents.filter(doc => doc.type !== 'contrat' && doc.type !== 'cv' && doc.type !== 'piece_identite' && doc.type !== 'diplome').length > 0 ? (
                      <div className="space-y-3">
                        {documents.filter(doc => doc.type !== 'contrat' && doc.type !== 'cv' && doc.type !== 'piece_identite' && doc.type !== 'diplome').map((doc, index) => (
                          <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200 hover:shadow-sm transition-shadow">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                                {getDocumentIcon(doc)}
                              </div>
                              <div>
                                <p className="font-semibold text-gray-800 text-sm">
                                  {doc.nom_fichier || `Document ${index + 1}`}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {doc.date_ajout ? new Date(doc.date_ajout).toLocaleDateString('fr-FR') : 'Date inconnue'}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleDownloadDocument(doc.id, doc.nom_fichier)}
                                className="p-2 hover:bg-blue-50 rounded-lg transition-colors group"
                                title="Télécharger"
                              >
                                <Download size={16} className="text-blue-600 group-hover:text-blue-700" />
                              </button>
                              <button
                                onClick={() => handleDeleteDocument(doc.id, doc.type)}
                                className="p-2 hover:bg-red-50 rounded-lg transition-colors group"
                                title="Supprimer"
                              >
                                <Trash2 size={16} className="text-red-600 group-hover:text-red-700" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl py-12 flex flex-col items-center">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                          <File size={32} className="text-gray-300" />
                        </div>
                        <p className="text-gray-500 mb-4 font-medium">Aucun document supplémentaire</p>
                        <button
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.multiple = true;
                            input.accept = 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
                            input.onchange = (e) => handleFileUpload(e, 'other');
                            input.click();
                          }}
                          className="flex items-center gap-2 px-5 py-2.5 bg-[#448696] text-white rounded-xl font-semibold text-sm hover:shadow-lg hover:-translate-y-0.5 transition-all"
                        >
                          <Plus size={16} />
                          Ajouter des documents
                        </button>
                      </div>
                    )}
                  </div>
                </div>

              </div>
            </Tabs.Content>
            </div>



            {/* FOOTER SIMPLE */}
            <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 flex justify-end items-center rounded-b-2xl bg-gray-50">
              <button
                onClick={onClose}
                className="px-6 py-2.5 rounded-xl font-semibold text-sm transition-all border border-gray-200 text-gray-700 hover:bg-gray-100"
              >
                Fermer
              </button>
            </div>
          </Tabs.Root>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default UserDetailModal;